import { apiClient } from './api'
import type { ApiResponse } from '@/types/common'

export interface Room {
  id: string
  batchId: string
  roomNumber: string
  capacity: number
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface RoomAssignment {
  id: string
  roomId: string
  batchId: string
  enrollmentId: string
  assignedBy?: string | null
  assignedAt: string
  specialNotes?: string
  createdAt: string
  updatedAt: string
}

export interface ListByBatchResponse {
  rooms: Room[] | null
  assignments: RoomAssignment[] | null
}

export interface AutoAssignResult {
  roomsCreated: number
  assignmentsCreated: number
  warnings: string[]
}

export interface MyRoomResponse {
  roomNumber: string
  capacity: number
  specialNotes?: string
}

export interface BatchEnrollment {
  enrollmentId: string
  userId: string
  name: string
  email: string
  gender?: string
  status: string
  enrolledAt: string
}

export const roomService = {
  listByBatch: async (
    batchId: string
  ): Promise<ApiResponse<ListByBatchResponse>> => {
    return apiClient.get(`/admin/batches/${batchId}/rooms`)
  },

  createRoom: async (
    batchId: string,
    roomNumber: string,
    capacity: number,
    notes?: string
  ): Promise<ApiResponse<Room>> => {
    return apiClient.post(`/admin/batches/${batchId}/rooms`, { roomNumber, capacity, notes: notes ?? '' })
  },

  autoAssign: async (
    batchId: string,
    overrideExisting: boolean,
    roomNames?: string[]
  ): Promise<ApiResponse<AutoAssignResult>> => {
    return apiClient.post(`/admin/batches/${batchId}/rooms/auto-assign`, {
      override_existing: overrideExisting,
      room_names: roomNames,
    })
  },

  moveAssignment: async (
    assignmentId: string,
    newRoomId: string
  ): Promise<ApiResponse<unknown>> => {
    return apiClient.patch(`/admin/room-assignments/${assignmentId}`, {
      new_room_id: newRoomId,
    })
  },

  removeAssignment: async (assignmentId: string): Promise<ApiResponse<unknown>> => {
    return apiClient.delete(`/admin/room-assignments/${assignmentId}`)
  },

  updateRoom: async (
    roomId: string,
    roomNumber: string,
    capacity: number,
    notes: string
  ): Promise<ApiResponse<unknown>> => {
    return apiClient.patch(`/admin/rooms/${roomId}`, { room_number: roomNumber, capacity, notes })
  },

  deleteRoom: async (roomId: string): Promise<ApiResponse<unknown>> => {
    return apiClient.delete(`/admin/rooms/${roomId}`)
  },

  addAssignment: async (
    roomId: string,
    enrollmentId: string,
    notes?: string
  ): Promise<ApiResponse<RoomAssignment>> => {
    return apiClient.post(`/admin/rooms/${roomId}/assignments`, {
      enrollment_id: enrollmentId,
      notes: notes ?? '',
    })
  },

  listBatchEnrollments: async (
    batchId: string
  ): Promise<ApiResponse<BatchEnrollment[]>> => {
    return apiClient.get(`/admin/batches/${batchId}/enrollments`)
  },

  // MyRoomResponse is the PDP-minimised participant shape (M6-H6). The
  // server no longer returns the internal AssignedBy pointer — peserta see
  // only the bits they need to find their room.
  getMyRoom: async (
    enrollmentId: string
  ): Promise<ApiResponse<MyRoomResponse | null>> => {
    return apiClient.get(`/me/enrollments/${enrollmentId}/room`)
  },
}

